import { useState, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import SuperAdminLayout from '@/components/admin/super-admin-layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePickerWithRange } from '@/components/ui/date-range-picker';
import { addDays, format, subDays, subMonths } from 'date-fns';
import { apiRequest } from '@/lib/queryClient';
import { 
  Download,
  Search,
  RefreshCw,
  Check,
  CreditCard,
  ChevronDown,
  ChevronUp,
  FileText,
  ExternalLink,
  Calendar,
  DollarSign,
  Building
} from 'lucide-react';

// Define invoice types
interface Invoice {
  id: string;
  tenantId: number;
  tenantName: string;
  stripeInvoiceId: string;
  amount: number;
  amountPaid: number;
  amountRemaining: number;
  currency: string;
  status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void';
  description: string;
  createdAt: string;
  dueDate: string | null;
  paidAt: string | null;
  period: {
    start: string;
    end: string;
  };
  subscriptionId: string | null;
  receiptUrl: string | null;
  invoiceUrl: string | null;
  paymentIntentId: string | null;
  paymentMethodId: string | null;
}

interface InvoiceSummary {
  totalRevenue: number;
  paidInvoices: number;
  dueInvoices: number;
  overdue: number;
  monthlyRevenue: {
    month: string;
    revenue: number;
  }[];
}

// Default data (would be replaced with API data)
const defaultInvoiceSummary: InvoiceSummary = {
  totalRevenue: 0,
  paidInvoices: 0,
  dueInvoices: 0,
  overdue: 0,
  monthlyRevenue: []
};

const InvoicesPage = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [dateRange, setDateRange] = useState({
    from: subMonths(new Date(), 1),
    to: new Date(),
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedInvoice, setExpandedInvoice] = useState<string | null>(null);
  
  // Fetch invoices
  const { data: invoices = [], isLoading: invoicesLoading } = useQuery<Invoice[]>({
    queryKey: ['/api/admin/system/invoices', { from: dateRange.from, to: dateRange.to, status: activeTab !== 'all' ? activeTab : undefined }],
    queryFn: async () => {
      try {
        // Use URL parameters for GET requests instead of body data
        const params = new URLSearchParams();
        params.append('fromDate', format(dateRange.from, 'yyyy-MM-dd'));
        params.append('toDate', format(dateRange.to, 'yyyy-MM-dd'));
        if (activeTab !== 'all') {
          params.append('status', activeTab);
        }
        
        const response = await apiRequest(`/api/admin/system/invoices?${params.toString()}`, {
          method: 'GET'
        });
        return response;
      } catch (error) {
        console.error('API error for GET /api/admin/system/invoices:', error);
        return [];
      }
    },
  });
  
  // Fetch invoice summary
  const { data: invoiceSummary = defaultInvoiceSummary, isLoading: summaryLoading } = useQuery<InvoiceSummary>({
    queryKey: ['/api/admin/system/invoice-summary', { from: dateRange.from, to: dateRange.to }],
    queryFn: async () => {
      try {
        // apiRequest already returns parsed JSON, so don't call json() again
        // Use URL parameters for GET requests instead of body data
        const params = new URLSearchParams();
        params.append('fromDate', format(dateRange.from, 'yyyy-MM-dd'));
        params.append('toDate', format(dateRange.to, 'yyyy-MM-dd'));
        
        const response = await apiRequest(`/api/admin/system/invoice-summary?${params.toString()}`, {
          method: 'GET'
        });
        return response;
      } catch (error) {
        console.error('Error fetching invoice summary:', error);
        return defaultInvoiceSummary;
      }
    },
  });
  
  // Filter invoices based on search query
  const filteredInvoices = invoices.filter(invoice => {
    return (
      invoice.tenantName.toLowerCase().includes(searchQuery.toLowerCase()) || 
      invoice.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.stripeInvoiceId.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });
  
  // Format currency
  const formatCurrency = (amount: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount / 100);
  };
  
  // Toggle invoice expanded state
  const toggleInvoiceExpanded = (invoiceId: string) => {
    if (expandedInvoice === invoiceId) {
      setExpandedInvoice(null);
    } else {
      setExpandedInvoice(invoiceId);
    }
  };
  
  // Predefined date ranges
  const handleLastWeek = () => {
    setDateRange({
      from: subDays(new Date(), 7),
      to: new Date()
    });
  };
  
  const handleLastMonth = () => {
    setDateRange({
      from: subMonths(new Date(), 1),
      to: new Date()
    });
  };
  
  const handleLast3Months = () => {
    setDateRange({
      from: subMonths(new Date(), 3),
      to: new Date()
    });
  };
  
  const handleYearToDate = () => {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    setDateRange({
      from: startOfYear,
      to: now
    });
  };
  
  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return format(new Date(dateString), 'MMM dd, yyyy');
  };
  
  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Paid</Badge>;
      case 'open':
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Open</Badge>;
      case 'draft':
        return <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">Draft</Badge>;
      case 'uncollectible':
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Uncollectible</Badge>;
      case 'void':
        return <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">Void</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };
  
  const isLoading = invoicesLoading || summaryLoading;
  
  return (
    <div className="min-h-screen bg-slate-50">
      <SuperAdminLayout>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Invoices & Billing</h1>
            <p className="text-slate-500">Manage and view all system invoices</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex gap-2">
              <Button size="sm" variant="outline" onClick={handleLastWeek}>
                7 days
              </Button>
              <Button size="sm" variant="outline" onClick={handleLastMonth}>
                30 days
              </Button>
              <Button size="sm" variant="outline" onClick={handleLast3Months}>
                3 months
              </Button>
              <Button size="sm" variant="outline" onClick={handleYearToDate}>
                Year to date
              </Button>
            </div>
            <DatePickerWithRange 
              date={dateRange}
              onDateChange={setDateRange}
            />
          </div>
        </div>
        
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Revenue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <DollarSign className="h-5 w-5 text-muted-foreground mr-2" />
                <div className="text-2xl font-bold">{formatCurrency(invoiceSummary.totalRevenue)}</div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Paid Invoices
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Check className="h-5 w-5 text-muted-foreground mr-2" />
                <div className="text-2xl font-bold">{invoiceSummary.paidInvoices}</div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Due Invoices
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Calendar className="h-5 w-5 text-muted-foreground mr-2" />
                <div className="text-2xl font-bold">{invoiceSummary.dueInvoices}</div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Overdue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <CreditCard className="h-5 w-5 text-muted-foreground mr-2" />
                <div className="text-2xl font-bold">{invoiceSummary.overdue}</div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Filter & Search */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full sm:w-auto">
            <TabsList className="grid w-full sm:w-auto grid-cols-5">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="paid">Paid</TabsTrigger>
              <TabsTrigger value="open">Open</TabsTrigger>
              <TabsTrigger value="draft">Draft</TabsTrigger>
              <TabsTrigger value="void">Void</TabsTrigger>
            </TabsList>
          </Tabs>
          
          <div className="flex w-full sm:w-auto gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input 
                type="search" 
                placeholder="Search invoices..." 
                className="pl-8 w-full sm:w-[250px]" 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button variant="outline" size="icon">
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {/* Invoices Table */}
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <RefreshCw className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredInvoices.length === 0 ? (
          <Card>
            <CardContent className="text-center py-16">
              <div className="flex justify-center">
                <FileText className="h-12 w-12 text-muted-foreground opacity-50" />
              </div>
              <h3 className="mt-4 text-lg font-medium">No invoices found</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                {searchQuery
                  ? `No results for "${searchQuery}". Try a different search term.`
                  : activeTab !== 'all'
                  ? `No ${activeTab} invoices found for the selected date range.`
                  : 'No invoices found for the selected date range.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="rounded-md border">
                <div className="overflow-x-auto">
                  <table className="w-full caption-bottom text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50 transition-colors hover:bg-slate-100">
                        <th className="h-12 px-4 text-left align-middle font-medium">Invoice ID</th>
                        <th className="h-12 px-4 text-left align-middle font-medium">Tenant</th>
                        <th className="h-12 px-4 text-left align-middle font-medium">Date</th>
                        <th className="h-12 px-4 text-left align-middle font-medium">Due Date</th>
                        <th className="h-12 px-4 text-left align-middle font-medium">Amount</th>
                        <th className="h-12 px-4 text-left align-middle font-medium">Status</th>
                        <th className="h-12 px-4 text-left align-middle font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInvoices.map((invoice) => (
                        <Fragment key={invoice.id}>
                          <tr 
                            className={`border-b transition-colors hover:bg-slate-100 ${expandedInvoice === invoice.id ? 'bg-slate-50' : ''}`}
                            onClick={() => toggleInvoiceExpanded(invoice.id)}
                          >
                            <td className="p-4 align-middle font-medium">
                              <div className="font-medium text-xs">{invoice.stripeInvoiceId}</div>
                            </td>
                            <td className="p-4 align-middle">
                              <div className="flex items-center">
                                <Building className="h-4 w-4 mr-2 text-muted-foreground" />
                                {invoice.tenantName}
                              </div>
                            </td>
                            <td className="p-4 align-middle">{formatDate(invoice.createdAt)}</td>
                            <td className="p-4 align-middle">{formatDate(invoice.dueDate)}</td>
                            <td className="p-4 align-middle font-medium">{formatCurrency(invoice.amount, invoice.currency)}</td>
                            <td className="p-4 align-middle">{getStatusBadge(invoice.status)}</td>
                            <td className="p-4 align-middle">
                              <div className="flex items-center gap-2">
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => {
                                  e.stopPropagation();
                                  if (invoice.invoiceUrl) window.open(invoice.invoiceUrl, '_blank');
                                }}>
                                  <ExternalLink className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  {expandedInvoice === invoice.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                </Button>
                              </div>
                            </td>
                          </tr>
                          {expandedInvoice === invoice.id && (
                            <tr className="border-b bg-slate-50">
                              <td colSpan={7} className="p-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                  <div>
                                    <h4 className="font-medium mb-2">Invoice Details</h4>
                                    <div className="space-y-2 text-sm">
                                      <div className="grid grid-cols-3 gap-2">
                                        <div className="text-muted-foreground">Description:</div>
                                        <div className="col-span-2">{invoice.description || 'N/A'}</div>
                                      </div>
                                      <div className="grid grid-cols-3 gap-2">
                                        <div className="text-muted-foreground">Period:</div>
                                        <div className="col-span-2">
                                          {formatDate(invoice.period.start)} - {formatDate(invoice.period.end)}
                                        </div>
                                      </div>
                                      <div className="grid grid-cols-3 gap-2">
                                        <div className="text-muted-foreground">Subscription:</div>
                                        <div className="col-span-2">{invoice.subscriptionId || 'N/A'}</div>
                                      </div>
                                      <div className="grid grid-cols-3 gap-2">
                                        <div className="text-muted-foreground">Payment ID:</div>
                                        <div className="col-span-2">{invoice.paymentIntentId || 'N/A'}</div>
                                      </div>
                                    </div>
                                  </div>
                                  <div>
                                    <h4 className="font-medium mb-2">Payment Information</h4>
                                    <div className="space-y-2 text-sm">
                                      <div className="grid grid-cols-3 gap-2">
                                        <div className="text-muted-foreground">Amount:</div>
                                        <div className="col-span-2 font-medium">{formatCurrency(invoice.amount, invoice.currency)}</div>
                                      </div>
                                      <div className="grid grid-cols-3 gap-2">
                                        <div className="text-muted-foreground">Paid:</div>
                                        <div className="col-span-2">{formatCurrency(invoice.amountPaid, invoice.currency)}</div>
                                      </div>
                                      <div className="grid grid-cols-3 gap-2">
                                        <div className="text-muted-foreground">Remaining:</div>
                                        <div className="col-span-2">{formatCurrency(invoice.amountRemaining, invoice.currency)}</div>
                                      </div>
                                      <div className="grid grid-cols-3 gap-2">
                                        <div className="text-muted-foreground">Paid On:</div>
                                        <div className="col-span-2">{invoice.paidAt ? formatDate(invoice.paidAt) : 'Not paid'}</div>
                                      </div>
                                      <div className="grid grid-cols-3 gap-2">
                                        <div className="text-muted-foreground">Method:</div>
                                        <div className="col-span-2">{invoice.paymentMethodId || 'N/A'}</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex justify-end gap-2 mt-4">
                                  {invoice.receiptUrl && (
                                    <Button variant="outline" size="sm" onClick={() => window.open(invoice.receiptUrl!, '_blank')}>
                                      View Receipt
                                    </Button>
                                  )}
                                  {invoice.invoiceUrl && (
                                    <Button variant="outline" size="sm" onClick={() => window.open(invoice.invoiceUrl!, '_blank')}>
                                      View Invoice
                                    </Button>
                                  )}
                                  <Button variant="outline" size="sm">
                                    <Download className="h-4 w-4 mr-1" />
                                    Download PDF
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between py-4">
              <div className="text-sm text-muted-foreground">
                Showing {filteredInvoices.length} of {invoices.length} invoices
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled>Previous</Button>
                <Button variant="outline" size="sm" disabled>Next</Button>
              </div>
            </CardFooter>
          </Card>
        )}
      </SuperAdminLayout>
    </div>
  );
};

export default InvoicesPage;